/**
 * PaymentLinkRevealModal — one-time URL reveal after link creation.
 * Security: url is shown ONCE; caller must clear reveal state on close.
 */

import { useState } from 'react';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import type { PaymentLinkCreateResponse } from '@aelita/shared';

interface PaymentLinkRevealModalProps {
  reveal: PaymentLinkCreateResponse | null;
  onClose: () => void;
}

export function PaymentLinkRevealModal({ reveal, onClose }: PaymentLinkRevealModalProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!reveal) return;
    try {
      await navigator.clipboard.writeText(reveal.url);
    } catch {
      // execCommand fallback
      const ta = document.createElement('textarea');
      ta.value = reveal.url;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleClose() {
    setCopied(false);
    onClose();
  }

  return (
    <Modal isOpen={!!reveal} onClose={handleClose} title="Payment Link Created" size="lg">
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
            Copy this link now — it will not be shown again.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={reveal?.url ?? ''}
            aria-label="Payment link URL"
            className="input flex-1 font-mono text-sm bg-gray-50 dark:bg-gray-800"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : 'Copy link'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shrink-0"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-green-700 dark:text-green-300">Copied</span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
        {reveal?.expiresAt && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Expires:{' '}
            {new Date(reveal.expiresAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </p>
        )}
        <div className="flex justify-end pt-2">
          <Button onClick={handleClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}
